/**
 * ⭐ **自助改密**（2026-10-09 · 用户："没有权限管理的人都没有改密码的地方？"）。
 *
 * 纪律（本会话归纳）：**凡是系统要求用户做的事，必须同时给出可到达的入口**。
 *  · 改自己的密码是**基本安全能力** ⇒ ⛔ **不挂任何功能点**（与登录/登出同级，不受 `settings_permissions` 阻断）；
 *  · `mustChangePassword === true` ⇒ **强制模式**：不可关闭（⛔ 无关闭按钮、⛔ 遮罩点击不关闭），
 *    因为后端此刻会拒掉改密与 `/auth/me` 之外的一切请求（`PASSWORD_CHANGE_REQUIRED`）；
 *  · 强度规则**以服务端为准** ⇒ 前端只校验"三项非空 + 两次一致"（⛔ 不另写一套强度校验）；
 *  · 失败**逐条可见**（直接用服务端 detail，例如"当前密码不正确"）。
 */
import { useState, type ReactElement } from "react";
import { ApiClientError } from "../lib/apiClient.js";
import { changeOwnPassword } from "../lib/session.js";

export interface ChangePasswordDialogProps {
  /** ⭐ `true` ⇒ 强制模式（不可关闭）。 */
  readonly forced: boolean;
  readonly onClose: () => void;
  readonly onSuccess?: () => void;
}

/** 失败原因逐条（服务端字段级 detail 优先，其次 message；⛔ 不吞错）。 */
function failureLinesOf(error: unknown): readonly string[] {
  if (error instanceof ApiClientError) {
    // 服务端字段级 detail：`{ path, issue }` ⇒ 呈现为「路径：问题」（⛔ 不吞、不折成泛泛一句）。
    const lines = error.details.map((detail) =>
      detail.path === "" ? detail.issue : `${detail.path}：${detail.issue}`,
    );
    return lines.length > 0 ? lines : [error.message];
  }
  return [error instanceof Error ? error.message : "修改密码失败"];
}

export function ChangePasswordDialog(props: ChangePasswordDialogProps): ReactElement {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState<readonly string[]>([]);
  const [done, setDone] = useState(false);

  /** ⛔ 前端只做"非空 + 两次一致"；强度规则以服务端为准。 */
  const localProblem =
    currentPassword === ""
      ? "请输入当前密码"
      : newPassword === ""
        ? "请输入新密码"
        : confirmPassword === ""
          ? "请再次输入新密码"
          : newPassword !== confirmPassword
            ? "两次输入的新密码不一致"
            : null;

  const submit = (): void => {
    if (localProblem !== null || busy) return;
    setLines([]);
    setBusy(true);
    void (async () => {
      try {
        await changeOwnPassword({ currentPassword, newPassword });
        setDone(true);
        props.onSuccess?.();
      } catch (error) {
        setLines(failureLinesOf(error));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div data-change-password="true" data-change-password-forced={props.forced ? "true" : "false"} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div role="dialog" aria-modal="true" aria-label="修改密码" className="w-80 rounded-lg border border-line bg-surface p-3">
        <h2 className="text-sm font-medium text-ink">修改密码</h2>
        {props.forced ? (
          <p className="mt-1 text-meta text-danger-text" data-change-password-forced-note="true">
            必须先修改初始口令才能继续使用（其他操作暂时被服务端拒绝）。
          </p>
        ) : null}

        {done ? (
          <p className="mt-2 text-meta text-ink" data-change-password-done="true">
            密码已修改。{props.forced ? "" : "下次登录请使用新密码。"}
          </p>
        ) : (
          <div className="mt-2 flex flex-col gap-1">
            <label className="flex flex-col gap-0.5 text-table text-ink">
              当前密码
              <input
                type="password"
                data-change-password-current="true"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="rounded-control border border-line px-1 text-table text-ink"
              />
            </label>
            <label className="flex flex-col gap-0.5 text-table text-ink">
              新密码
              <input
                type="password"
                data-change-password-new="true"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="rounded-control border border-line px-1 text-table text-ink"
              />
            </label>
            <label className="flex flex-col gap-0.5 text-table text-ink">
              确认新密码
              <input
                type="password"
                data-change-password-confirm="true"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="rounded-control border border-line px-1 text-table text-ink"
              />
            </label>
            {/* ⭐ 失败/本地校验**逐条可见**（⛔ 不静默）。 */}
            {localProblem === null || currentPassword === "" ? null : (
              <p className="text-meta text-warning-text" data-change-password-local-problem="true">
                {localProblem}
              </p>
            )}
            {lines.length === 0 ? null : (
              <ul data-change-password-failure-lines="true" className="flex flex-col gap-0.5 text-meta text-danger-text">
                {lines.map((line, index) => (
                  <li key={`${line}:${String(index)}`}>{line}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-3 flex justify-end gap-1">
          {/* ⛔ 强制模式**没有关闭按钮**（否则用户被闸门锁死却把弹窗关掉）。 */}
          {props.forced ? null : (
            <button
              type="button"
              data-change-password-cancel="true"
              onClick={props.onClose}
              className="rounded-control border border-line px-2 py-0.5 text-meta text-ink"
            >
              取消
            </button>
          )}
          {done ? (
            <button
              type="button"
              data-change-password-finish="true"
              onClick={props.onClose}
              className="rounded-control border border-line px-2 py-0.5 text-meta text-ink"
            >
              完成
            </button>
          ) : (
            <button
              type="button"
              data-change-password-submit="true"
              disabled={busy || localProblem !== null}
              title={localProblem ?? undefined}
              onClick={submit}
              className="rounded-control border border-line px-2 py-0.5 text-meta text-ink disabled:opacity-50"
            >
              {busy ? "提交中…" : "确认修改"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
